// src/components/store/sections/product-cards/MagazineEditorialProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight, ShoppingBag, Star, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const EDITORIAL_THEMES = [
  { bg: "from-slate-900 via-slate-800 to-zinc-900", accent: "#f43f5e", tag: "VOGUE EDIT" },
  { bg: "from-indigo-950 via-slate-900 to-purple-950", accent: "#38bdf8", tag: "LUXE ISSUE" },
  { bg: "from-emerald-950 via-slate-900 to-teal-950", accent: "#34d399", tag: "RUNWAY PICK" },
  { bg: "from-rose-950 via-slate-900 to-amber-950", accent: "#fbbf24", tag: "LIMITED ED." },
];

export default function MagazineEditorialProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    added,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = EDITORIAL_THEMES[index % EDITORIAL_THEMES.length];

  return (
    <div
      className={`relative w-full h-full min-h-[410px] rounded-[28px] overflow-hidden bg-gradient-to-b ${theme.bg} text-white p-4 flex flex-col justify-between shadow-xl hover:shadow-2xl group select-none border border-white/10`}
    >
      {/* Giant Editorial Background Watermark */}
      <span className="absolute top-8 inset-x-0 text-center text-6xl font-serif font-black uppercase tracking-tighter text-white/5 pointer-events-none select-none truncate">
        {categoryName}
      </span>

      {/* Magazine Masthead */}
      <div className="relative z-10 flex items-center justify-between border-b border-white/15 pb-2.5">
        <span
          style={{ color: theme.accent }}
          className="text-[10px] font-serif italic font-black tracking-widest uppercase"
        >
          {theme.tag}
        </span>
        <span className="flex items-center gap-1 text-[10px] font-mono text-white/80">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {rating.toFixed(1)}
        </span>
      </div>

      {/* Center Hero Product Visual */}
      <Link href={productUrl} className="relative z-10 w-full h-48 my-2 flex items-center justify-center block">
        <div className="relative w-40 h-40 group-hover:scale-110 transition-transform duration-500">
          <Image
            src={productImg}
            alt={productName}
            fill
            unoptimized
            className="object-contain drop-shadow-[0_20px_25px_rgba(0,0,0,0.7)]"
          />
        </div>
      </Link>

      {/* Frosted Editorial Overlay Footer */}
      <div className="relative z-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-3.5 space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="text-[9px] uppercase tracking-widest text-white/60 block">
              {categoryName}
            </span>
            <Link href={productUrl} className="block">
              <h3 className="text-sm font-serif font-bold text-white truncate hover:underline">
                {productName}
              </h3>
            </Link>
          </div>
          <Link
            href={productUrl}
            className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white shrink-0"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-white/10">
          <span className="text-base font-black font-mono text-white">
            ৳{currentPrice.toLocaleString()}
          </span>

          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            style={{ backgroundColor: added ? "#10b981" : theme.accent }}
            className="px-3.5 py-1.5 rounded-xl text-slate-950 font-black text-[10px] uppercase tracking-wider shadow-md active:scale-95 transition-all cursor-pointer flex items-center gap-1"
          >
            {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
            <span>{added ? "ADDED" : "COLLECT"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
